import axiosInstance from '@/services/axiosInstance'
import { AdditionalCostRequest, ObjectTypeCode, RequestStatusCode } from '@/types/AdditionalCostRequest'
import AdditionalCostRequestStatus from '@/constants/AdditionalCostRequestStatus'
import { PaginationParams } from '@/types/filter/PaginationParams'

// FE chỉ gửi fee_id + amount + lý do của dòng; mã phí / tên phí / nhóm phí do BE tra từ danh mục rồi
// snapshot lại, và `reason` tổng của đề xuất cũng do BE ghép từ lý do các dòng này.
export type AdditionalCostRequestFeePayload = {
  fee_id: number
  amount: number
  reason?: string | null
  // true = khi duyệt sẽ cộng thêm vào chi phí sẵn có của lô, false/bỏ qua = ghi đè
  is_accumulate?: boolean
}

// 5 filter phát sinh thêm (0055-loc-nguoi-gui-otc-bod-ngay-de-xuat-ngay-den-diem-dong-hang.md):
// requested_by/reviewed_by/approved_by lọc theo đúng username (không phải user_id), match tuyệt đối.
// requested_at/vn_to_loading_point_date lọc theo đúng 1 ngày (yyyy-MM-dd), BE tự quy về khoảng
// [00:00:00.000, 23:59:59.999] của ngày đó. vn_to_loading_point_date chỉ tồn tại trên VehicleTracking
// -> khi đang lọc theo field này, mọi đề xuất object_type=CUSTOMS_DECLARATION tự động bị loại (hành vi
// mong đợi từ BE, không phải lỗi).
export type AdditionalCostRequestFilters = {
  object_type?: ObjectTypeCode | null
  object_id?: number | null
  status?: RequestStatusCode | null
  code?: string | null
  requested_by?: string | null
  reviewed_by?: string | null
  approved_by?: string | null
  requested_at?: string | null
  vn_to_loading_point_date?: string | null
}

export const apiGetAdditionalCostRequestList = async (params: AdditionalCostRequestFilters) => {
  const response = await axiosInstance.get('/api/v1/additional-cost-requests/list', { params })
  return response.data
}

export const apiGetAdditionalCostRequestPagination = async (
  filters: AdditionalCostRequestFilters,
  pagination: PaginationParams,
) => {
  const { page, size } = pagination
  const response = await axiosInstance.get('/api/v1/additional-cost-requests/pagination', {
    params: { ...filters, page, size },
  })
  return response.data
}

export const apiGetAdditionalCostRequest = async (id: number) => {
  const response = await axiosInstance.get(`/api/v1/additional-cost-requests/${id}`)
  return response.data
}

// `fees`: danh sách dòng phí phát sinh (phí đầu vào). Khi có fees, BE tự tính amount = tổng các dòng
// và bỏ qua amount FE gửi lên.
export const apiCreateAdditionalCostRequest = async (data: {
  object_id: number
  object_type: ObjectTypeCode
  amount: number
  // Không còn gửi: BE tự ghép từ lý do của từng dòng phí. Giữ optional cho các chỗ gọi cũ.
  reason?: string
  fees?: AdditionalCostRequestFeePayload[]
  input_arise?: string | null
  output_arise?: string | null
}) => {
  const response = await axiosInstance.post('/api/v1/additional-cost-requests', data, {
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data
}

export const apiUpdateAdditionalCostRequest = async (
  id: number,
  data: {
    object_id: number
    object_type: ObjectTypeCode
    amount: number
    // Không còn gửi: BE tự ghép từ lý do của từng dòng phí
    reason?: string
    // Danh sách đầy đủ sau khi sửa — BE ghi đè toàn bộ danh sách cũ (bỏ qua trường này = giữ nguyên)
    fees?: AdditionalCostRequestFeePayload[]
    input_arise?: string | null
    output_arise?: string | null
  },
) => {
  const response = await axiosInstance.patch(`/api/v1/additional-cost-requests/${id}`, data, {
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data
}

export const apiProcessAdditionalCostRequest = async (
  id: number,
  data: {
    status:
      | typeof AdditionalCostRequestStatus.PENDING_BOD
      | typeof AdditionalCostRequestStatus.REJECTED_BY_OCT
      | typeof AdditionalCostRequestStatus.APPROVED_BY_BOD
      | typeof AdditionalCostRequestStatus.REJECTED_BY_BOD
    note?: string | null
    // Kết quả duyệt TỪNG dòng phí của lớp đang xử lý: lớp OCT gửi oct_*, lớp BOD gửi bod_* (BE chỉ
    // đọc cặp field đúng với lớp hiện tại). `id` là id của dòng phí trong `fees[]` của đề xuất,
    // không phải fee_id. Bỏ trống = giữ nguyên trạng thái duyệt hiện tại của các dòng phí.
    //
    // `amount` / `is_accumulate` CHỈ lớp BOD được gửi (lớp OCT gửi lên cũng bị BE bỏ qua): BOD sửa
    // được 2 ô này ngay trên popup phê duyệt vì quyết định cuối là của BOD và số BOD chốt mới là số
    // được đẩy vào phí của lô. BE GHI ĐÈ thẳng lên số người gửi nhập (không giữ số gốc) rồi tính lại
    // `amount` tổng của đề xuất. Bỏ trống từng field = giữ nguyên giá trị đang có.
    fees?: (
      | { id: number; oct_approved: boolean; oct_note?: string | null }
      | {
          id: number
          bod_approved: boolean
          bod_note?: string | null
          amount?: number
          is_accumulate?: boolean
        }
    )[]
  },
) => {
  const response = await axiosInstance.patch(`/api/v1/additional-cost-requests/${id}/process`, data, {
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data
}

// Cập nhật riêng 3 ô ghi chú "Ghi chú người gửi" / "Ghi chú OCT" / "Ghi chú BOD" (`request_note` /
// `review_note` / `approve_note`) sửa thẳng trên bảng danh sách. KHÁC hẳn PATCH /{id}/process: endpoint
// này chỉ đụng đúng các cột đó, không yêu cầu `status`/role, không ảnh hưởng luồng thẩm định/phê duyệt
// chính thức. Key vắng mặt = giữ nguyên giá trị cũ, value null hoặc chuỗi rỗng = xoá ghi chú.
// Xem docs/for-backend/0010-ghi-chu-otc-bod-de-xuat-phat-sinh.md.
export const apiUpdateAdditionalCostRequestExtra = async (
  id: number,
  data: { request_note?: string | null; review_note?: string | null; approve_note?: string | null },
) => {
  const response = await axiosInstance.patch(`/api/v1/additional-cost-requests/${id}/extra`, data, {
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data
}

export const apiDeleteAdditionalCostRequest = async (id: number) => {
  const response = await axiosInstance.delete(`/api/v1/additional-cost-requests/${id}`)
  return response.data
}

export const apiBulkDeleteAdditionalCostRequests = async (ids: number[]) => {
  const response = await axiosInstance.delete('/api/v1/additional-cost-requests/batch', {
    data: ids,
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data
}

// Số đếm badge cạnh menu "Đề xuất phát sinh" - xem docs/receive-backend/0017-badge-de-xuat-phat-sinh.md.
export interface AdditionalCostRequestNotificationCounts {
  oct_count: number
  bod_count: number
  approved_count: number
}

export const apiGetAdditionalCostRequestNotificationCounts = async () => {
  const response = await axiosInstance.get('/api/v1/additional-cost-requests/notification-counts')
  return response.data
}

// Đánh dấu đã đọc TOÀN BỘ approved_count liên quan tới user hiện tại - gọi 1 lần ngay sau khi menu
// "Đề xuất phát sinh" mở xong (load /pagination xong), không cần id/body.
export const apiMarkAdditionalCostRequestNotificationsRead = async () => {
  const response = await axiosInstance.patch('/api/v1/additional-cost-requests/mark-notifications-read')
  return response.data
}

export type { AdditionalCostRequest }
